import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { cssUrl } from '../utils/cssVersion';
import { fetchCasualAccess, fetchCasualAvailability, submitCasualAvailability, respondToAssignment, fetchCasualThreads, sendCasualMessage, markCasualThreadSeen, updateCasualSlot } from '../utils/api';
import usePollWhenVisible from '../utils/usePollWhenVisible';
import { isTimeLabel, parseTimeLabel } from '../utils/successMatch';
import MessagesPanel, { ThreadModal, useLockBody } from '../components/MessagesPanel';
import { FoldGroup, FOLD_PEEK } from '../components/shared/FoldGroup';
import { melTodayISO, fmtRange, fmtShort, fmtDayNum, timeToLabel, dayName } from '../utils/melDate';
import AvailabilityBuilder from '../components/AvailabilityBuilder';
import VideoAvailableLibrary from '../components/VideoAvailableLibrary';
import { useToasts, ToastStack } from '../components/Toasts';
import { DOCKLANDS, homeBaseForPostcode } from '../data/vicSuburbs';

/**
 * CasualsAvailability
 *
 * Mobile / iPad (route /workavailabilitym):
 *   default export — one stacked page: header, on-page builder (five range
 *   buttons → build → review), then the availability table with jobs.
 *
 * Desktop (your FilterAvailablestaffd page with TabBar + InnerTabsd):
 *   wrap InnerTabsd in <CasualsAvailabilityProvider> and give the inner tabs
 *   the three exported components — CasualsCreate, CasualsMine, CasualsJobs.
 *   InnerTabsd renders each with no props; they share one state through the
 *   provider, so a submit on Create shows up on My Availability straight away.
 *
 * Data: /api/casual/availability (GET on load, POST on submit) and
 * PATCH /api/casual/assignment/:id for Accept / Decline. The server returns
 * entries already grouped into the builder's block shape.
 * Add ?demo=1 to the URL to see a local example entry with three jobs.
 */

const ACCENT = '#F97316';           // executem accent
const STATUS_LABEL = { pending: 'Pending', accepted: 'Accepted', declined: 'Declined', approved: 'Done', cancelled: 'Withdrawn' };
/* Assigned Jobs is a read-only running tally of the lifecycle, grouped by
   stage in this order; Done lives on its own tab. Accept / Decline happen in
   My Availability, where the casual can also widen their hours. */
const JOB_STAGES = [
  { id: 'pending',   title: 'Pending — waiting for your answer in My Availability' },
  { id: 'accepted',  title: 'Accepted — you are rostered' },
  { id: 'declined',  title: 'Declined by you' },
  { id: 'cancelled', title: 'Withdrawn by a supervisor' },
  { id: 'expired',   title: 'Expired — the day passed with no answer' },
];

/* A job still Pending after its day has gone is Expired: nothing can be done
   with it any more, so it drops out of the Pending count and sits on its own. */
const stageOf = (a, today) => (a.status === 'pending' && a.date && a.date < today ? 'expired' : a.status);
const DEFAULT_POSTCODE = '3000';

/* One colour per date on an availability record, so a job row's left edge
   says which day it belongs to at a glance. Ten is plenty — a record is a
   week at most — and it wraps if it ever isn't. */
const DATE_COLORS = ['#22d3ee', '#f97316', '#a78bfa', '#34d399', '#f472b6',
  '#fbbf24', '#60a5fa', '#fb7185', '#4ade80', '#c084fc'];

/* ── shared state for the tab components ─────────────────────── */
const PageCtx = createContext(null);
const usePage = () => useContext(PageCtx);
export const useCasualPage = usePage;      // the tab badges (pages/casualTabBadges.jsx) read the same provider

/* load the versioned stylesheet once */
const useStylesheet = (href) => {
  useEffect(() => {
    if (document.querySelector('link[data-ca-css]')) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet'; link.href = href; link.dataset.caCss = '1';
    document.head.appendChild(link);
  }, [href]);
};

/* builder payload → table rows: one row per block (a day, a week, or a week of the month) */
const payloadToEntries = (payload) =>
  payload.blocks.map((b, i) => ({
    id: `${Date.now()}-${i}`,
    submittedAt: payload.createdAt,
    mode: payload.mode,
    homeBase: payload.homeBase,
    location: b.location,            // { suburb, postcode, la, lo }
    estimatedKm: b.estimatedKm,
    label: b.label,
    weekStart: b.weekStart,
    weekEnd: b.weekEnd,
    individual: b.individual,
    slots: b.slots,
    assignments: [],
  }));

/* Summary for the list: suburb only, date range only, "Varies by day" when
   times differ, radius span, day chips. */
const entrySummary = (e, today) => {
  const slots = e.slots || [];
  const days = slots.map((s) => `${s.day} ${fmtDayNum(s.date)}`);
  const starts = slots.map((s) => s.start).filter(Boolean);
  const ends = slots.map((s) => s.end).filter(Boolean);
  const sameTimes = starts.length > 0 && starts.every((x) => x === starts[0]) && ends.every((x) => x === ends[0]);
  const time = !starts.length ? '' : sameTimes ? `${timeToLabel(starts[0])} – ${timeToLabel(ends[0])}` : 'Varies by day';
  const radii = [...new Set(slots.map((s) => Number(s.radius)).filter((r) => !Number.isNaN(r)))].sort((a, b) => a - b);
  const radius = radii.length === 0 ? '' : radii.length === 1 ? `${radii[0]} km` : `${radii[0]}–${radii[radii.length - 1]} km`;
  const relative = (iso) => (iso === today ? 'today' : iso === addDaysISO(today, 1) ? 'tomorrow' : '');
  return { days, time, sameTimes, radius, relative, count: slots.length };
};

/* Day chips: 5+ consecutive days collapse to one range chip ("Mon – Fri");
   4 or fewer, or any gap in the run, list every day. */
const dayChips = (slots) => {
  const sorted = [...slots].sort((a, b) => a.date.localeCompare(b.date));
  const consecutive = sorted.every((sl, i) => i === 0 || sl.date === addDaysISO(sorted[i - 1].date, 1));
  if (consecutive && sorted.length >= 5) {
    return [{ key: 'range', label: `${sorted[0].day} – ${sorted[sorted.length - 1].day}` }];
  }
  return sorted.map((sl) => ({ key: sl.date, label: sl.day }));
};

const addDaysISO = (iso, n) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
};

const AVAIL_STATUS = {
  Open: { label: 'Open', cls: 'open' },
  Assigned: { label: 'Job assigned', cls: 'assigned' },
  Withdrawn: { label: 'Withdrawn', cls: 'withdrawn' },
  Expired: { label: 'Expired', cls: 'expired' },
};

/* ── demo data while the backend is being built ────────────── */
const demoEntries = (today) => {
  const p = {
    mode: 'week', homeBase: null, createdAt: new Date().toISOString(),
    blocks: [{
      label: 'This week', weekStart: today, weekEnd: today, individual: false,
      location: { suburb: DOCKLANDS.s, postcode: DOCKLANDS.p, la: DOCKLANDS.la, lo: DOCKLANDS.lo }, estimatedKm: 10,
      slots: [{ date: today, day: dayName(today), start: '08:00', end: '16:00', radius: 10 }],
    }],
  };
  const [entry] = payloadToEntries(p);
  entry.assignments = [
    { id: 'a1', date: today, jobAddress: '12 Harbour Esplanade, Docklands', jobStart: '09:00', jobFinish: '13:00', contact: 'Sam (Supervisor) 0400 000 000', notes: 'Vacate clean, 2 bedrooms', status: 'pending' },
    { id: 'a2', date: today, jobAddress: '800 Collins St, Docklands', jobStart: '13:30', jobFinish: '15:30', contact: 'Sam (Supervisor) 0400 000 000', notes: 'Office common area', status: 'pending' },
    { id: 'a3', date: today, jobAddress: '2 Bourke St, Docklands', jobStart: '16:00', jobFinish: '18:00', contact: 'Priya 0411 111 111', notes: 'Bring keys from office', status: 'pending' },
  ];
  return [entry];
};

/* address + suburb without repeating the suburb */
const addrLine = (address, suburb) => {
  const a = String(address || '').trim().replace(/,\s*$/, '');
  const sb = String(suburb || '').trim();
  if (!sb) return a;
  if (!a) return sb;
  return a.toLowerCase().includes(sb.toLowerCase()) ? a : `${a}, ${sb}`;
};

/* ─────────────────────────────────────────────────────────────
   Assignment row (compact) + detail modal — casual side
   ───────────────────────────────────────────────────────────── */
/* Accept / Decline pair: one press only — both buttons lock and the pressed
   one shows "Processing…" until the server answers. `busy` = 'accepted' |
   'declined' | '' from the provider (per assignment id).

   Accept is ALSO locked while `a.acceptBlocked` — the day's jobs do not fit
   inside the hours this casual posted, so they have to widen their
   availability first. Decline always stays live: nobody should be stuck with
   a job they cannot do. The same rule is enforced in respond_to_job. */
const RespondButtons = ({ a, busy, onRespond, compact = true }) => {
  const locked = !!busy;
  const cls = compact ? 'ca-btn' : 'ca-btn';
  const blocked = !!a.acceptBlocked;
  return (
    <div className="ca-job-actions">
      <button type="button" className={`${cls} decline ${busy === 'declined' ? 'is-busy' : ''}`} disabled={locked} aria-busy={busy === 'declined'} onClick={() => onRespond(a.id, 'declined')}>
        {busy === 'declined' && <span className="sv-spin" aria-hidden="true" />}{busy === 'declined' ? 'Processing…' : 'Decline'}
      </button>
      <button type="button" className={`${cls} accept ${busy === 'accepted' ? 'is-busy' : ''}`}
        disabled={locked || blocked} aria-busy={busy === 'accepted'}
        title={blocked ? a.acceptNote : undefined}
        onClick={() => onRespond(a.id, 'accepted')}>
        {busy === 'accepted' && <span className="sv-spin" aria-hidden="true" />}{busy === 'accepted' ? 'Processing…' : 'Accept'}
      </button>
    </div>
  );
};

/* Why Accept is greyed out, spelled out where the casual can act on it */
const AcceptBlockedNote = ({ a }) => (!a.acceptBlocked ? null : (
  <div className="ca-blocked">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" aria-hidden="true"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="13" /><line x1="12" y1="16" x2="12" y2="16" /></svg>
    <span>{a.acceptNote}</span>
  </div>
));

const AssignmentRow = ({ a, onOpen, onRespond, busy = '', readOnly = false, stage, noNote = false, msgCount = null }) => (
  <div className={`ca-job ca-job-${stage || a.status} ca-job-row`} onClick={() => onOpen(a)} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && onOpen(a)}>
    <div className="ca-job-time">{timeToLabel(a.jobStart)}<br /><span className="ca-job-time-end">to {timeToLabel(a.jobFinish)}</span></div>
    <div className="ca-job-main">
      <div className="ca-job-title">
        <span className="ca-job-title-text">{a.jobName ? `${a.jobName} · ` : ''}{addrLine(a.jobAddress, a.jobSuburb)}</span>
        {/* message total on the job-name line: right end, same colour as the name */}
        {msgCount != null && <span className="ca-msg-count">{msgCount} message{msgCount === 1 ? '' : 's'}</span>}
      </div>
      <div className="ca-job-date">{fmtShort(a.date)} · Click to view record ›</div>
    </div>
    <div className="ca-job-side" onClick={(e) => e.stopPropagation()}>
      {a.status === 'pending' && !readOnly ? (
        <RespondButtons a={a} busy={busy} onRespond={onRespond} />
      ) : (
        <span className={`ca-status ${stage || a.status}`}>{STATUS_LABEL[stage || a.status] || stage || a.status}</span>
      )}
    </div>
    {a.status === 'pending' && !readOnly && !noNote && <div className="ca-job-blocked" onClick={(e) => e.stopPropagation()}><AcceptBlockedNote a={a} /></div>}
  </div>
);

const CasualJobModal = ({ a, onClose, onRespond, busy = '', readOnly = false }) => {
  const { openMessage } = usePage();
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  useLockBody();
  if (!a) return null;
  const F = ({ label, value, wide }) => (
    <label className={`ca-job-field ${wide ? 'ca-job-wide' : ''}`}><span className="ca-label">{label}</span><input value={value || ''} readOnly /></label>
  );
  return (
    <div className="ca-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="ca-rec" role="dialog" aria-modal="true" aria-label="Job">
        <div className="ca-rec-head">
          <div className="ca-rec-title">{a.jobName || 'Job'}<span className="ca-rec-dot"> · </span><span className="ca-rec-dates">{fmtShort(a.date)}</span></div>
          <div className="ca-rec-sub"><span className="ca-rec-home">{timeToLabel(a.jobStart)} – {timeToLabel(a.jobFinish)}</span></div>
          <span className={`ca-status ${a.status} ca-rec-status`}>{STATUS_LABEL[a.status] || (a.status === 'pending' ? 'Pending' : a.status)}</span>
        </div>
        <div className="ca-rec-body">
          <div className="ca-job-grid">
            <F label="Job address" value={addrLine(a.jobAddress, a.jobSuburb)} wide />
            <F label="Job start" value={timeToLabel(a.jobStart)} />
            <F label="Job finish" value={timeToLabel(a.jobFinish)} />
            <F label="Contact person" value={a.contact} wide />
            <label className="ca-job-field ca-job-wide"><span className="ca-label">Notes</span><textarea value={a.notes || ''} readOnly rows={3} /></label>
            {a.assignedBy && <F label="Assigned by" value={a.assignedBy} wide />}
            {a.declineReason && <F label="Declined because" value={a.declineReason} wide />}
          </div>
        </div>
        {a.status === 'pending' && !readOnly && <div className="ca-rec-body"><AcceptBlockedNote a={a} /></div>}
        <div className="ca-rec-foot">
          {/* available at every stage — a casual often needs to ask something
              BEFORE they can accept or decline */}
          <button type="button" className="ca-btn ca-msg-job" onClick={() => openMessage(a)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                 strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            Send message
          </button>
          {a.status === 'pending' && !readOnly && (
            <RespondButtons a={a} busy={busy} onRespond={async (id, status) => { await onRespond(id, status); onClose(); }} compact={false} />
          )}
          <button type="button" className="ca-btn ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

/* ── Job Messages bell ───────────────────────────────────────────
   Sits in the Work availability header and in each availability record, so a
   casual can pick up a supervisor's message about a job without leaving the
   page they are working on. */
const MessagesBell = ({ unread = 0, onClick }) => (
  /* NB: classes are ca-msgbtn-*, NOT ca-bell-* — MessagesPanel already owns
     .ca-bell for its own inline icon bells and this would clobber them. */
  <button type="button" className="ca-msgbtn" onClick={(e) => { e.stopPropagation(); onClick(); }}
    title={unread ? `${unread} unread job message${unread === 1 ? '' : 's'}` : 'Job messages'}>
    <span className="ca-msgbtn-label">Job Messages</span>
    <span className="ca-msgbtn-icon">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
           strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
      {unread > 0 && <span className="ca-msgbtn-dot">{unread}</span>}
    </span>
  </button>
);

const JobMessagesModal = ({ onClose }) => {
  const { threads, threadsLoading, access, sendMessage, openThread, refreshThreads } = usePage();
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  useLockBody();
  return (
    <div className="ca-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="ca-rec ca-msg-modal" role="dialog" aria-modal="true" aria-label="Job messages">
        <div className="ca-rec-head"><div className="ca-rec-title">Job messages</div></div>
        <div className="ca-msg-scroll">
          <MessagesPanel threads={threads} loading={threadsLoading} me={access?.name}
            onSend={sendMessage} onOpen={openThread} onRefresh={refreshThreads} accent={ACCENT} side="casual" />
        </div>
        <div className="ca-rec-foot"><button type="button" className="ca-btn ghost" onClick={onClose}>Close</button></div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   Record modal — one availability (a week range) with its jobs
   ───────────────────────────────────────────────────────────── */
const RecordModal = ({ entry, today, homeSuburb, onClose, onRespond, busyIds = {} }) => {
  const { unreadTotal, setMsgOpen, editSlots, msgCountOf } = usePage();
  const [job, setJob] = useState(null);
  /* Edit mode turns the Day / Start / End table into inputs. Times are always
     editable — widening them is how a casual takes a job that runs past what
     they first offered. The DATE is only editable while that day carries no
     job, because moving it would take the job with it. */
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [editErr, setEditErr] = useState('');
  const heldIds = new Set((entry.assignments || [])
    .filter((a) => ['pending', 'accepted', 'approved'].includes(a.status))
    .map((a) => a.availabilityId));

  /* jobs grouped by the day they fall on, earliest first; one day opens by
     default when that is all there is, otherwise they start closed */
  const jobsByDate = useMemo(() => {
    const m = new Map();
    (entry.assignments || []).forEach((a) => {
      const k = a.date || '';
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(a);
    });
    return [...m.entries()].sort((x, y) => (x[0] || '').localeCompare(y[0] || ''));
  }, [entry.assignments]);
  const [openDay, setOpenDay] = useState(null);      // every day group starts closed

  const startEdit = () => {
    setDraft(Object.fromEntries(entry.slots.map((sl) => [sl.recordId, {
      date: sl.date, start: timeToLabel(sl.start) || '', end: timeToLabel(sl.end) || '',
    }])));
    setEditErr('');
    setEditing(true);
  };
  const saveEdit = async () => {
    setEditErr('');
    const bad = entry.slots.find((sl) => !isTimeLabel(draft[sl.recordId]?.start) || !isTimeLabel(draft[sl.recordId]?.end));
    if (bad) { setEditErr('Times must read like 8:00 AM or 12:30 PM'); return; }
    setSaving(true);
    try {
      await editSlots(entry.slots.map((sl) => ({
        recordId: sl.recordId,
        date: heldIds.has(sl.recordId) ? undefined : draft[sl.recordId].date,
        start: parseTimeLabel(draft[sl.recordId].start),
        end: parseTimeLabel(draft[sl.recordId].end),
      })));
      setEditing(false);
    } catch (e) { setEditErr(e.message); }
    finally { setSaving(false); }
  };
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  useLockBody();
  if (!entry) return null;
  const s = entrySummary(entry, today);
  const st = AVAIL_STATUS[entry.status] || AVAIL_STATUS.Open;
  const pending = entry.assignments.filter((a) => a.status === 'pending').length;
  const declined = entry.assignments.filter((a) => ['declined', 'cancelled'].includes(a.status)).length;
  return (
    <div className="ca-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="ca-rec ca-rec-wide" role="dialog" aria-modal="true" aria-label="Availability record">
        <div className="ca-rec-head ca-rec-head-actions">
          {/* row 1: how many days + the date(s) */}
          <div className="ca-rec-title">
            {s.count} day{s.count === 1 ? '' : 's'}
            <span className="ca-rec-dot" aria-hidden="true"> · </span>
            <span className="ca-rec-dates">{s.count === 1 ? fmtShort(entry.weekStart) : fmtRange(entry.weekStart, entry.weekEnd)}</span>
          </div>
          {/* row 2: home suburb → job location suburb */}
          <div className="ca-rec-sub">
            <span className="ca-rec-home">{entry.homeBase?.suburb || homeSuburb || 'Home'}</span>
            <svg className="ca-rec-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-label="to"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
            <span className="ca-rec-job">{entry.location?.suburb}</span>
            {s.radius && <span className="ca-rec-km">{s.radius}</span>}
          </div>
          <div className="ca-rec-actions">
            <MessagesBell unread={unreadTotal} onClick={() => setMsgOpen(true)} />
            <span className={`ca-avail-status ${st.cls} ca-rec-status`}>{st.label}</span>
          </div>
        </div>

        <div className="ca-rec-body">
          <table className="ca-slots ca-rec-table">
            <thead><tr><th>Day</th><th>Start</th><th>End</th></tr></thead>
            <tbody>
              {entry.slots.map((sl) => {
                const rel = s.relative(sl.date);
                const d = draft[sl.recordId] || {};
                const held = heldIds.has(sl.recordId);
                const set = (k) => (v) => setDraft((p) => ({ ...p, [sl.recordId]: { ...p[sl.recordId], [k]: v } }));
                if (!editing) {
                  return (
                    <tr key={sl.recordId}>
                      <td>{fmtShort(sl.date)}{rel && <span className="ca-rel"> {rel}</span>}</td>
                      <td>{timeToLabel(sl.start)}</td>
                      <td>{timeToLabel(sl.end)}</td>
                    </tr>
                  );
                }
                return (
                  <tr key={sl.recordId} className="ca-edit-row">
                    <td>
                      {held ? (
                        <>{fmtShort(sl.date)}<div className="ca-rel">job assigned — date locked</div></>
                      ) : (
                        <input type="date" className="ca-edit-in" min={today} value={d.date || ''} onChange={(e) => set('date')(e.target.value)} />
                      )}
                    </td>
                    <td><input className={`ca-edit-in ${d.start && !isTimeLabel(d.start) ? 'ca-edit-bad' : ''}`} value={d.start || ''} placeholder="8:00 AM" onChange={(e) => set('start')(e.target.value)} /></td>
                    <td><input className={`ca-edit-in ${d.end && !isTimeLabel(d.end) ? 'ca-edit-bad' : ''}`} value={d.end || ''} placeholder="4:00 PM" onChange={(e) => set('end')(e.target.value)} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="ca-rec-jobs">
            {entry.assignments.length === 0 ? (
              <div className="ca-no-jobs">No jobs assigned to this availability yet.</div>
            ) : (
              <div className="ca-jobs">
                <div className="ca-jobs-title">
                  {pending ? `${pending} job${pending === 1 ? '' : 's'} waiting for your answer` : declined ? `${declined} previous job${declined === 1 ? '' : 's'} on these days (declined / cancelled)` : 'All jobs answered'}
                </div>
                {jobsByDate.map(([date, list], i) => {
                  const open = openDay === date;
                  /* every job on a day carries the same note, so show it once
                     on the group's face — visible whether open or closed */
                  const note = list.find((a) => a.acceptBlocked)?.acceptNote || '';
                  return (
                    <div key={date} className={`ca-daygrp ${open ? 'is-open' : ''}`}
                      style={{ '--day': DATE_COLORS[i % DATE_COLORS.length] }}>
                      <button type="button" className="ca-daygrp-head" aria-expanded={open}
                        onClick={() => setOpenDay(open ? null : date)}>
                        <svg className="ca-daygrp-caret" width="14" height="14" viewBox="0 0 24 24" fill="none"
                             stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><polyline points="9 18 15 12 9 6" /></svg>
                        <span className="ca-daygrp-title">Jobs for {fmtShort(date)}</span>
                        <span className="ca-daygrp-count">{list.length}</span>
                        {/* the day's message total = the sum of its jobs' counts below */}
                        <span className="ca-daygrp-msgs">{list.reduce((n, a) => n + msgCountOf(a.id), 0)} message{list.reduce((n, a) => n + msgCountOf(a.id), 0) === 1 ? '' : 's'}</span>
                        {note && <span className="ca-daygrp-note">{note}</span>}
                        {note && (
                          <span className="ca-daygrp-cta">
                            Click to display records
                            <span className="ca-daygrp-cta-alt"> · Use Edit times below</span>
                          </span>
                        )}
                      </button>
                      {open && (
                        <div className="ca-daygrp-body">
                          {list.map((a) => (
                            <AssignmentRow key={a.id} a={a} noNote busy={busyIds?.[a.id] || ''} onOpen={setJob} msgCount={msgCountOf(a.id)}
                              onRespond={(id, status) => onRespond(entry.id, id, status)} />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {editErr && <div className="ca-rec-body"><div className="ca-error">{editErr}</div></div>}
        <div className="ca-rec-foot">
          {editing ? (
            <>
              <button type="button" className="ca-btn ghost" disabled={saving} onClick={() => { setEditing(false); setEditErr(''); }}>Cancel edit</button>
              <button type="button" className={`ca-btn accept ${saving ? 'is-busy' : ''}`} disabled={saving} onClick={saveEdit}>
                {saving && <span className="sv-spin" aria-hidden="true" />}{saving ? 'Saving…' : 'Save times'}
              </button>
            </>
          ) : (
            <button type="button" className="ca-btn ca-foot-left" onClick={startEdit}>Edit times</button>
          )}
          <button type="button" className="ca-btn ghost" onClick={onClose}>Close</button>
        </div>
      </div>
      {job && <CasualJobModal a={job} busy={busyIds[job.id] || ''} onClose={() => setJob(null)} onRespond={(id, status) => onRespond(entry.id, id, status)} />}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   Pieces (used by both layouts; read shared state from context)
   ───────────────────────────────────────────────────────────── */
const PageHeader = ({ messages = false, title = 'Work availability' }) => {
  const { access, homeBase, homePostcode, today, unreadTotal, setMsgOpen } = usePage();
  return (
    <div className="ca-head">
      {messages && (
        <div className="ca-head-msg">
          <MessagesBell unread={unreadTotal} onClick={() => setMsgOpen(true)} />
        </div>
      )}
      <div className="ca-head-text">
        <h1 className="ca-title">{title}</h1>
        <div className="ca-head-meta">
          <span>{access.name}</span>
          {access.workingWithChildren && <span className="ca-wwc">Working with children</span>}
          <span className="ca-head-home">Home Base {homeBase ? `${homeBase.suburb} ${homeBase.postcode}` : homePostcode}</span>
          <span className="ca-head-date">{fmtShort(today)}</span>
        </div>
      </div>
    </div>
  );
};

const Builder = () => {
  const { handleSubmit, today, homePostcode, takenDates } = usePage();
  return <AvailabilityBuilder onSubmit={handleSubmit} today={today} homePostcode={homePostcode}
    accent={ACCENT} takenDates={takenDates} />;
};

const AvailabilityTable = () => {
  const { entries, entriesLoading, viewing, setViewing, respond, homeBase, today, busyIds } = usePage();
  const open = entries.find((e) => e.id === viewing) || null;
  if (entriesLoading && entries.length === 0) {
    return <div className="ca-skeleton" />;
  }
  if (entries.length === 0) {
    return (
      <div className="ca-empty">
        <div className="ca-empty-title">No availability posted yet</div>
        <p>Pick a range to tell the office where you can work, when, and how far you'll travel from {homeBase?.suburb || 'home'}. Jobs assigned to you will show up here to accept or decline.</p>
      </div>
    );
  }
  return (
    <>
      <div className="ca-table-wrap">
        <table className="ca-table">
          <thead>
            <tr>
              <th>Job Location</th>
              <th>Dates</th>
              <th>Days</th>
              <th>Start – End</th>
              <th>Home – Job</th>
              <th className="ca-th-jobs">Jobs</th>
              <th className="ca-th-status">Status</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => {
              const s = entrySummary(e, today);
              const pending = e.assignments.filter((a) => a.status === 'pending').length;
              const st = AVAIL_STATUS[e.status] || AVAIL_STATUS.Open;
              return (
                <React.Fragment key={e.id}>
                  <tr className="ca-tr" onClick={() => setViewing(e.id)}>
                    <td data-label="Job Location" className="ca-td-loc">
                      <div className="ca-td-label">{e.location?.suburb}</div>
                    </td>
                    <td data-label="Dates" className="ca-td-dates">
                      {s.count === 1 ? fmtShort(e.weekStart) : fmtRange(e.weekStart, e.weekEnd)}
                    </td>
                    <td data-label="Days" className="ca-td-dayscell">
                      <div className="ca-td-days">
                        {dayChips(e.slots).map((c) => (
                          <span key={c.key} className="ca-td-chip">{c.label}</span>
                        ))}
                      </div>
                    </td>
                    <td data-label="Start – End" className="ca-td-time">{s.time}</td>
                    <td data-label="Home – Job" className="ca-td-radius">{s.radius}</td>
                    <td data-label="Jobs" className="ca-td-jobs">
                      {e.assignments.length ? (
                        <span className={`ca-count ${pending ? 'live' : ''}`} style={pending ? { background: ACCENT } : undefined}>
                          {pending || e.assignments.length}
                        </span>
                      ) : (
                        <span className="ca-count none">0</span>
                      )}
                    </td>
                    <td data-label="Status" className="ca-td-status">
                      <span className={`ca-avail-status ${st.cls}`}>{st.label}</span>
                    </td>
                  </tr>
                  <tr className="ca-tr-foot" onClick={() => setViewing(e.id)}>
                    <td colSpan={7}>
                      <button type="button" className="ca-view-link">
                        Click here to view record
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                      </button>
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {open && <RecordModal entry={open} today={today} homeSuburb={homeBase?.suburb} onClose={() => setViewing(null)} onRespond={respond} busyIds={busyIds} />}
    </>
  );
};

/* Assigned Jobs — every job except Done, grouped by stage with a header per stage */
const JobsPanel = () => {
  const { jobs, today, msgCountOf } = usePage();
  const [job, setJob] = useState(null);
  const [openStage, setOpenStage] = useState(null);              // folded by default; ONE open at a time
  /* read-only: every job except Done, with its lifecycle stage resolved */
  const active = jobs.filter((a) => a.status !== 'approved').map((a) => ({ ...a, stage: stageOf(a, today) }));
  const awaiting = active.filter((a) => a.stage === 'pending').length;
  return (
    <div className="ca-jobs-panel">
      {active.length === 0 ? (
        <div className="ca-empty">
          <div className="ca-empty-title">No jobs assigned yet</div>
          <p>When a supervisor assigns a job against one of your availability days it appears here. Accept or decline it from My Availability.</p>
        </div>
      ) : (
        <>
          <div className="ca-jobs-title">
            {awaiting
              ? `${awaiting} job${awaiting === 1 ? '' : 's'} waiting for your answer — accept or decline them in My Availability`
              : 'All jobs answered'}
          </div>
          {JOB_STAGES.map((st) => {
            const list = active.filter((a) => a.stage === st.id);
            if (!list.length) return null;
            const open = openStage === st.id;
            return (
              <FoldGroup key={st.id} title={st.title} count={list.length} accent={ACCENT} open={open}
                         onToggle={() => setOpenStage((cur) => (cur === st.id ? null : st.id))}>
                <div className="ca-jobs">
                  {(open ? list : list.slice(0, FOLD_PEEK)).map((a) => (
                    <AssignmentRow key={a.id} a={a} readOnly stage={a.stage} onOpen={setJob} msgCount={msgCountOf(a.id)} />
                  ))}
                </div>
              </FoldGroup>
            );
          })}
        </>
      )}
      {job && <CasualJobModal a={job} readOnly onClose={() => setJob(null)} />}
    </div>
  );
};

/* Jobs Completed — Done only, most recent first */
const CompletedPanel = () => {
  const { jobs, msgCountOf } = usePage();
  const [job, setJob] = useState(null);
  const done = jobs.filter((a) => a.status === 'approved').sort((x, y) => (y.date || '').localeCompare(x.date || ''));
  // one fold per MONTH, most recent first; folded by default, ONE open at a time
  const [openMonth, setOpenMonth] = useState(null);
  const months = [];                                              // [{ key: YYYY-MM, label, list }]
  done.forEach((a) => {
    const key = (a.date || '').slice(0, 7) || 'No date';
    let g = months.find((m) => m.key === key);
    if (!g) {
      const label = key === 'No date' ? 'No date' : new Date(Number(key.slice(0, 4)), Number(key.slice(5, 7)) - 1, 1).toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });
      g = { key, label, list: [] }; months.push(g);
    }
    g.list.push(a);
  });
  return (
    <div className="ca-jobs-panel">
      {done.length === 0 ? (
        <div className="ca-empty">
          <div className="ca-empty-title">No completed jobs yet</div>
          <p>Once a supervisor marks a job you did as done it moves here.</p>
        </div>
      ) : (
        <>
          <div className="ca-jobs-title">{done.length} completed job{done.length === 1 ? '' : 's'}</div>
          {months.map((m) => {
            const open = openMonth === m.key;
            return (
              <FoldGroup key={m.key} title={m.label} count={m.list.length} accent={ACCENT} open={open}
                         onToggle={() => setOpenMonth((cur) => (cur === m.key ? null : m.key))}>
                <div className="ca-jobs">{(open ? m.list : m.list.slice(0, FOLD_PEEK)).map((a) => <AssignmentRow key={a.id} a={a} readOnly onOpen={setJob} msgCount={msgCountOf(a.id)} />)}</div>
              </FoldGroup>
            );
          })}
        </>
      )}
      {job && <CasualJobModal a={job} readOnly onClose={() => setJob(null)} />}
    </div>
  );
};

/* Messages — one thread per job */
const MessagesTab = () => {
  const { threads, threadsLoading, access, sendMessage, openThread, refreshThreads } = usePage();
  return (
    <MessagesPanel
      threads={threads}
      loading={threadsLoading}
      me={access?.name}
      onSend={sendMessage}
      onOpen={openThread}
      onRefresh={refreshThreads}
      accent={ACCENT}
      side="casual"
    />
  );
};

/* ── exported for the desktop InnerTabsd page (rendered with no props) ── */
/* each tab's card header carries that tab's own name, not a generic one */
export const CasualsCreate = () => <div className="ca-tab-pane"><PageHeader title="Create Availability" /><VideoAvailableLibrary leg="staff" /><Builder /></div>;
export const CasualsMine   = () => <div className="ca-tab-pane"><PageHeader messages title="My Availability" /><AvailabilityTable /></div>;
export const CasualsJobs      = () => <div className="ca-tab-pane"><PageHeader title="Assigned Jobs" /><JobsPanel /></div>;
export const CasualsCompleted = () => <div className="ca-tab-pane"><PageHeader title="Jobs Completed" /><CompletedPanel /></div>;
export const CasualsMessages  = () => <div className="ca-tab-pane"><PageHeader title="Messages" /><MessagesTab /></div>;

/* ─────────────────────────────────────────────────────────────
   Provider — access check + entries + handlers, shared by every layout
   ───────────────────────────────────────────────────────────── */
export const CasualsAvailabilityProvider = ({ children }) => {
  useStylesheet(cssUrl('casualsAvailability.css'));

  const [access, setAccess] = useState(null);      // { casual, name, workingWithChildren, today, homePostcode }
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [jobs, setJobs] = useState([]);                // every assignment (Assigned Jobs tab)
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [viewing, setViewing] = useState(null);      // entry id open in the record modal
  const [busyIds, setBusyIds] = useState({});         // assignmentId → 'accepted' | 'declined' while a response is in flight
  const [threads, setThreads] = useState([]);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const { toasts, push, pushEmail, fail, dismiss } = useToasts();
  const [msgOpen, setMsgOpen] = useState(false);       // Job Messages modal
  const [messagingJob, setMessagingJob] = useState(null);   // job whose thread is open

  const today = access?.today || melTodayISO();
  const homePostcode = access?.homePostcode || DEFAULT_POSTCODE;
  const homeBase = useMemo(() => homeBaseForPostcode(homePostcode), [homePostcode]);

  useEffect(() => {
    let alive = true;
    fetchCasualAccess()
      .then((a) => alive && setAccess(a))
      .catch(() => alive && setAccess({ casual: false }))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  // load this casual's availability + jobs once access is confirmed
  useEffect(() => {
    if (!access?.casual) return undefined;
    if (new URLSearchParams(window.location.search).get('demo') === '1') {
      setEntries(demoEntries(access.today));
      return undefined;
    }
    let alive = true;
    setEntriesLoading(true);
    fetchCasualAvailability()
      .then((r) => { if (alive) { setEntries(r.entries || []); setJobs(r.jobs || []); } })
      .catch((e) => alive && fail(e))
      .finally(() => alive && setEntriesLoading(false));
    return () => { alive = false; };
  }, [access, fail]);        // `fail` is a stable useCallback — it never retriggers the load

  const showToast = (msg) => push(msg);

  const handleSubmit = async (payload) => {
    try {
      const r = await submitCasualAvailability(payload);
      setEntries(r.entries || []);
      setJobs(r.jobs || []);
      setViewing(null);
      if (r.replay) {
        showToast('Already sent — this availability was saved a moment ago');
        return;                                  // no second email either
      }
      showToast(`Availability sent for ${r.created} day${r.created === 1 ? '' : 's'}`);
      const dropped = [...(r.skipped?.duplicate || []), ...(r.skipped?.past || [])];
      if (dropped.length) {
        // say so — silently dropping a day the casual asked for is worse than
        // the extra toast
        push(`${dropped.length} day${dropped.length === 1 ? '' : 's'} not added: `
          + [...new Set(dropped)].sort().map(fmtShort).join(', ')
          + ' — already posted, or in the past', 'err', 0);
      }
      pushEmail(r.email);                        // "Email sent to … for Availability Posted"
    } catch (e) {
      fail(e);
      throw e;                                   // keeps the builder on the review step
    }
  };

  /* Save one or more edited availability days. Each row is its own record, so
     this is a PATCH per changed row; the last response carries the fresh
     lists. Throws with the server's reason so the modal can show it. */
  const editSlots = async (rows) => {
    let last = null;
    for (const r of rows) {
      const body = { start: r.start, end: r.end };
      if (r.date) body.date = r.date;
      last = await updateCasualSlot(r.recordId, body);   // throws on 400
    }
    if (last) { setEntries(last.entries || []); setJobs(last.jobs || []); }
    push(`Availability updated for ${rows.length} day${rows.length === 1 ? '' : 's'}`);
    return last;
  };

  const loadThreads = () => {
    setThreadsLoading(true);
    return fetchCasualThreads().then((r) => setThreads(r.threads || [])).catch(() => {}).finally(() => setThreadsLoading(false));
  };
  // Poll only while the tab is visible: 30 s while someone is doing things here, easing off to
  // 5 min when idle, nothing at all when hidden (one immediate read when it comes back). Every
  // deliberate refresh — mount, the Refresh button, after a send — counts as activity.
  const pollBump = usePollWhenVisible(loadThreads, { enabled: !!access?.casual });
  const refreshThreads = () => { pollBump(); return loadThreads(); };
  useEffect(() => { if (access?.casual) refreshThreads(); }, [access]);
  useEffect(() => () => { document.body.style.overflow = ''; }, []);   // never leave the page locked

  // optimistic: show the message straight away, then confirm from the server.
  // Airtable lookups on a brand-new row can lag a second or two, so the
  // confirming refresh is delayed rather than fired immediately.
  const sendMessage = async (assignmentId, text) => {
    const r = await sendCasualMessage(assignmentId, text);   // throws with the server's reason
    pushEmail(r.email);                          // "Email sent to … for Job Message"
    const m = { sender: 'Casual', ...(r.message || { id: `tmp-${Date.now()}`, from: access?.name, to: '', text, status: 'New', createdAt: new Date().toISOString() }) };
    setThreads((prev) => {
      // First message on this job? There is no thread to append to yet, so make
      // one — otherwise the message vanishes until the refresh lands.
      const exists = prev.some((t) => t.assignmentId === assignmentId);
      const base = exists ? prev : [...prev, {
        assignmentId, casual: access?.name || '', jobLocation: '', jobDate: null,
        messages: [], unread: 0, status: 'Seen',
      }];
      return base.map((t) => {
        if (t.assignmentId !== assignmentId) return t;
        // Append by id, never blindly: the server's double-submit guard hands
        // back the SAME message for a repeated send, and appending it twice
        // showed two bubbles for one message until the refresh cleaned up.
        if (t.messages.some((x) => x.id === m.id)) return t;
        return { ...t, messages: [...t.messages, m], lastFrom: m.from, lastTo: m.to, lastAt: m.createdAt };
      });
    });
    setTimeout(refreshThreads, 1500);
  };
  const openThread = async (assignmentId) => {
    pollBump();                                          // reading a thread = active here
    await markCasualThreadSeen(assignmentId);
    setThreads((prev) => prev.map((t) => (t.assignmentId !== assignmentId ? t : {
      ...t, unread: 0, status: 'Seen',
      messages: t.messages.map((m) => (m.sender !== 'Casual' ? { ...m, status: 'Seen' } : m)),
    })));
  };

  const respond = async (entryId, assignmentId, status) => {
    if (busyIds[assignmentId]) return;                   // second tap while processing is ignored
    setBusyIds((b) => ({ ...b, [assignmentId]: status }));
    try {
      const r = await respondToAssignment(assignmentId, status);
      const updated = r.assignment;
      setEntries((prev) => prev.map((e) => (e.id !== entryId ? e : {
        ...e, assignments: e.assignments.map((a) => (a.id === assignmentId ? { ...a, ...updated } : a)),
      })));
      setJobs((prev) => prev.map((a) => (a.id === assignmentId ? { ...a, ...updated } : a)));
      showToast(status === 'accepted' ? 'Job accepted' : 'Job declined');
      pushEmail(r.email);                        // "Email sent to … for Job Accepted / Job Declined"
      // a decline reopens the day; reload so My Availability reflects the server state
      fetchCasualAvailability().then((r2) => { setEntries(r2.entries || []); setJobs(r2.jobs || []); }).catch(() => {});
    } catch (e) {
      fail(e);
    } finally {
      setBusyIds((b) => { const n = { ...b }; delete n[assignmentId]; return n; });
    }
  };

  /* Dates this casual already has a live availability row on — one row per
     date is the rule, so Create Availability greys out a range whose every
     day is taken and leaves the taken days out of the ones that are not.
     Withdrawn days free their date again, matching the server. */
  const takenDates = useMemo(() => new Set(entries
    .flatMap((e) => e.slots || [])
    .filter((sl) => (sl.status || 'Open') !== 'Withdrawn')
    .map((sl) => sl.date)), [entries]);

  /* Message about one job, from anywhere it is shown. A job with no thread yet
     gets an empty one so the casual can open the conversation themselves —
     which is the point: they may need to ask before they can answer. */
  const openMessage = (job) => setMessagingJob(job);
  const messagingThread = useMemo(() => {
    if (!messagingJob) return null;
    return threads.find((t) => t.assignmentId === messagingJob.id) || {
      assignmentId: messagingJob.id,
      casual: messagingJob.assignedBy || 'Supervisor',
      jobLocation: messagingJob.jobName || messagingJob.jobSuburb || messagingJob.jobAddress || 'Job',
      jobDate: messagingJob.date,
      messages: [], unread: 0, status: 'Seen',
    };
  }, [messagingJob, threads]);

  const pendingTotal = useMemo(() => jobs.filter((a) => stageOf(a, today) === 'pending').length, [jobs, today]);
  const unreadTotal = useMemo(() => threads.reduce((n, t) => n + (t.unread || 0), 0), [threads]);
  // messages on one job, from the threads already loaded — shown on every job row and summed on the day folds
  const msgCountOf = (id) => (threads.find((t) => t.assignmentId === id)?.messages?.length) || 0;
  const doneTotal = useMemo(() => jobs.filter((a) => a.status === 'approved').length, [jobs]);

  // My Availability keeps a day until every job on it has been answered with
  // an Accept. While ANY job there is still Pending the day stays put, so the
  // casual can see (and widen) the hours those offers are asking for — with
  // two or three jobs on a day, all of them must be accepted before it goes.
  // Declined / cancelled records stay attached to the day (see the modal).
  const openEntries = useMemo(() => entries
    .map((e) => {
      const live = {};
      e.assignments.forEach((a) => {
        if (['pending', 'accepted', 'approved'].includes(a.status)) {
          (live[a.availabilityId] = live[a.availabilityId] || []).push(a);
        }
      });
      const held = new Set(Object.keys(live).filter((id) => live[id].every((a) => a.status !== 'pending')));
      const slots = e.slots.filter((sl) => !held.has(sl.recordId));
      return { ...e, slots, heldCount: e.slots.length - slots.length };
    })
    .filter((e) => e.slots.length > 0), [entries]);
  const openDays = useMemo(() => openEntries.reduce((n, e) => n + e.slots.length, 0), [openEntries]);   // My Availability badge

  if (loading) {
    return <div className="ca-page"><div className="ca-skeleton" /><div className="ca-skeleton short" /></div>;
  }

  if (!access?.casual) {
    return (
      <div className="ca-page">
        <div className="ca-locked">
          <div className="ca-locked-title">Work availability isn't set up for this login</div>
          <p>Only casual staff listed under Casual Work Positions can post availability. If you think that's you, check the email and mobile on your account match what the office has on file.</p>
        </div>
      </div>
    );
  }

  const ctx = { access, homeBase, homePostcode, today, entries: openEntries, allEntries: entries, jobs, entriesLoading, viewing, setViewing, handleSubmit, respond, pendingTotal,
    busyIds, threads, threadsLoading, unreadTotal, sendMessage, openThread, refreshThreads,
    msgOpen, setMsgOpen, editSlots, takenDates, openMessage, msgCountOf, doneTotal, openDays };

  return (
    <PageCtx.Provider value={ctx}>
      {children}
      {msgOpen && <JobMessagesModal onClose={() => setMsgOpen(false)} />}
      {messagingThread && (
        <ThreadModal thread={messagingThread} me={access?.name} side="casual" accent={ACCENT}
          onSend={sendMessage} onClose={() => setMessagingJob(null)} />
      )}
      <ToastStack toasts={toasts} onDismiss={dismiss} />
    </PageCtx.Provider>
  );
};

/* ─────────────────────────────────────────────────────────────
   Default export — stacked page for phone + iPad (/workavailabilitym)
   ───────────────────────────────────────────────────────────── */
const CasualsAvailability = () => (
  <CasualsAvailabilityProvider>
    <div className="ca-page">
      <PageHeader messages />
      <Builder />
      <h2 className="ca-section-title">My availability</h2>
      <AvailabilityTable />
    </div>
  </CasualsAvailabilityProvider>
);

export default CasualsAvailability;